import { TemplateElement, html, defineElement, when, classMap, unsafeHTML } from '@webtides/element-js';
import style from './hero-slider.css';
import '../../../button/button.js';
import '../../../chip/chip.js';
import '../../../icon/icon.js';
import '../../../slider/slider.js';
import { responsiveImage } from '../../../../directives/images.js';
import { shuffle } from '../../../../utils/array.js';

/**
 * @typedef {Object} SalesElementProperties
 * @property {string} title
 * @property {string} icon
 * @property {ImageProperties} image
 * @property {string} position
 * @property {number} distance
 * @property {number} shift
 */

/**
 * @typedef {Object} SlideProperties
 * @property {string} [chipText]
 * @property {string} [chipVariant]
 * @property {string} [kicker]
 * @property {string} headline
 * @property {string} [text]
 * @property {LinkProperties} link
 * @property {ImageProperties} image
 * @property {string} [imageBadge]
 * @property {NotificationProperties} notification
 * @property {SalesElementProperties[]} [salesElements]
 */

/**
 * @property {string} backgroundColor
 * @property {ImageProperties} backgroundImage
 * @property {boolean} autoplay
 * @property {number} autoplayDelay
 * @property {boolean} randomize
 * @property {SlideProperties[]} slides
 */
export default class HeroSlider extends TemplateElement {
    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            backgroundColor: '',
            backgroundImage: undefined,
            autoplay: false,
            autoplayDelay: 5000,
            randomize: false,
            slides: [
                {
                    chipText: '',
                    chipVariant: '',
                    kicker: '',
                    headline: '',
                    text: '',
                    link: undefined,
                    image: undefined,
                    imageBadge: '',
                    notification: undefined,
                    salesElements: [],
                },
            ],
        };
    }

    connected() {
        if (this.randomize && Array.isArray(this.slides) && this.slides.length > 1) {
            this.slides = shuffle(this.slides);
        }
    }

    template() {
        const bgColorClasses = {
            'bg-green-medium': this.backgroundColor === 'green-medium',
            'bg-green-dark': this.backgroundColor === 'green-dark',
            'bg-violet-medium': this.backgroundColor === 'violet-medium',
            'bg-violet-dark': this.backgroundColor === 'violet-dark',
        };
        // On mobile the background box stops short of the bottom so the slide image and
        // pagination dots sit below it; on desktop it fills the full height.
        const bgWrapperClasses = {
            'bottom-112 md:bottom-0': this.slides.length > 1,
            'bottom-56 md:bottom-0': this.slides.length <= 1,
        };
        return html`
            <div class="container mt-fix-xl">
                <div class="text-white relative" data-surface="dark">
                    <div
                        class="absolute inset-x-0 top-0 z-10 rounded-md md:rounded-lg overflow-hidden ${classMap(
                            bgWrapperClasses,
                        )}"
                    >
                        <div class="absolute inset-0 ${classMap(bgColorClasses)}"></div>
                        ${when(
                            this.backgroundImage?.srcset,
                            responsiveImage(
                                this.backgroundImage,
                                'absolute inset-0 w-full h-full object-cover object-left-top',
                                false,
                                'high',
                                '(min-width: 1440px) 1360px, 100vw',
                                'eager',
                            ),
                        )}
                    </div>
                    <div class="relative py-24 md:py-64 md:pb-24 z-20">
                        <mb-slider
                            class="block navigation-on-default md:[--pagination-color:#d9d9d980] md:[--pagination-color-active:#d9d9d9]"
                            pagination="true"
                            navigation="false"
                            slides-per-view="1"
                            space-between="64"
                            breakpoints=""
                            swiper-options="${JSON.stringify({
                                autoplay: this.autoplay
                                    ? { delay: this.autoplayDelay, pauseOnMouseEnter: true }
                                    : false,
                            })}"
                        >
                            ${this.slides.map((slide, index) => this._renderSlide(slide, index))}
                        </mb-slider>
                    </div>
                </div>
            </div>
        `;
    }

    _renderSlide(slide, index) {
        const useSalesElements = slide.imageBadge === 'salesElements' && slide.salesElements?.length > 0;
        // empty imageBadge = legacy slide predating the badge selector; keep showing its notification
        const useNotification =
            !useSalesElements &&
            (slide.imageBadge === 'notification' || (!slide.imageBadge && !!slide.notification?.text));
        const link = slide.link || {};
        return html`
            <div slot="slide-${index}" class="h-full flex overflow-x-clip px-16 md:px-80">
                <div class="grid grid-cols-1 md:grid-cols-10 gap-24 md:gap-16 items-center">
                    <div class="col-span-full md:col-span-6">
                        ${when(
                            slide.chipText,
                            html`
                                <mb-chip
                                    variant="${slide.chipVariant || 'accent'}"
                                    text="${slide.chipText}"
                                    class="mb-8"
                                ></mb-chip>
                            `,
                        )}
                        ${when(!slide.chipText && slide.kicker, html`<div class="mb-8">${slide.kicker}</div>`)}
                        <h1 class="mb-24 text-balance text-[32px] lg:text-[60px]">${slide.headline}</h1>
                        ${when(slide.text, html`<p class="mb-24">${slide.text}</p>`)}
                        ${when(
                            link.url,
                            html`
                                <mb-button
                                    href="${link.url}"
                                    element="hero slider"
                                    tracking-label="${link.trackingLabel || ''}"
                                    variant="primary"
                                >
                                    ${link.text}
                                    <mb-icon slot="suffix" name="navigation--chevron-right" class="size-12"></mb-icon>
                                </mb-button>
                            `,
                        )}
                    </div>
                    <div class="col-span-full md:col-span-4 relative pb-24 md:pb-0">
                        ${when(
                            slide.image?.srcset,
                            responsiveImage(
                                slide.image,
                                'rounded-sm w-full aspect-video md:aspect-[5/4] object-cover ml-auto',
                                false,
                                index === 0 ? 'high' : 'auto',
                                '(min-width: 1440px) 480px, (min-width: 768px) 35vw, 100vw',
                                'eager',
                            ),
                        )}
                        ${when(useNotification, this._renderNotification(slide.notification))}
                        ${when(useSalesElements, this._renderSalesElements(slide.salesElements || []))}
                    </div>
                </div>
            </div>
        `;
    }

    _renderNotification(notification) {
        return html`
            <div
                class="absolute left-16 bottom-0 right-16 md:left-0 md:right-0 md:bottom-24 md:w-5/6 lg:w-2/3 bg-green-light25 text-green-medium p-fix-s flex gap-fix-s rounded-sm"
            >
                ${when(
                    notification?.icon,
                    html`<mb-icon name="${notification?.icon}" class="size-24 text-green-medium"></mb-icon>`,
                )}
                <div class="text-sm">
                    <p class="mb-0">${notification?.text}</p>
                    ${when(
                        notification?.link?.url,
                        html`
                            <a
                                href="${notification?.link?.url}"
                                class="underline"
                                element="hero slider"
                                tracking-label="${notification?.link?.trackingLabel || ''}"
                                >${notification?.link?.text}</a
                            >
                        `,
                    )}
                </div>
            </div>
        `;
    }

    _renderSalesElements(salesElements) {
        return html`${salesElements.map((label, index) => {
            const hasImage = !!label.image?.srcset;
            const hasIcon = !!label.icon;
            return html`
                <div
                    class="label ${label.position}-label ${when(
                        index > 0,
                        'hidden',
                        '',
                    )} md:block bg-primary-subtle1 lg:px-fix-s px-fix-xs rounded-[var(--mdx-sys-border-radius-default)] max-w-[160px] ${when(
                        hasImage,
                        'py-fix-2xs',
                        'py-fix-s',
                    )}"
                    style="--distance: ${label.distance}%; --shift: ${label.shift}%;"
                >
                    ${when(
                        hasImage,
                        responsiveImage(label.image, '-mt-fix-m pb-fix-xs mx-auto', false, 'auto', '130px', 'eager'),
                    )}
                    ${when(
                        !hasImage && hasIcon,
                        html`
                            <mb-icon
                                size="inherit"
                                name="${label.icon}"
                                class="h-32 size-32 object-cover mx-auto text-green-medium lg:py-fix-2xs py-fix-3xs lg:mb-fix-2xs mb-fix-3xs"
                            ></mb-icon>
                        `,
                    )}
                    <div
                        class="richtext md:text-center text-green-medium ${when(
                            !hasImage && !hasIcon,
                            'has-no-image',
                            '',
                        )}"
                    >
                        ${unsafeHTML(label.title || '')}
                    </div>
                </div>
            `;
        })}`;
    }
}

defineElement('mb-hero-slider', HeroSlider);
